import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button, FamilyTag, StatusDot, Avatar, ScoreChip, DifficultyBars, fmtScore, useEscape, useToast,
} from '../../components/ui';
import { typeOfExamType } from '../../config/assessmentFamilies';
import { examSetAPI } from '../../services/api';
import { SKILL_AREA_LABELS } from '../../components/campaign/GroupedQuestionList';
import { codingLanguageLabel } from '../../utils/codingLanguages';
import { statsFor, fmtDate, resultDateText, hasReport } from './derive';
import { sendSingleInvite, sendBulkInvite, resetCandidateSession } from './actions';
import ReportModal from './ReportModal';

const BUCKETS = ['0–20', '20–40', '40–60', '60–80', '80–100'];

/** One candidate in the panel's Candidates tab (T0 item J actions). */
function PersonRow({ p, r, examType, checked, onCheck, busyId, onSend, onReset, onReport }) {
  const invite = String(r.row.invite_status || 'pending').toLowerCase();
  const eligible = invite === 'pending' && !String(r.row.id).startsWith('synced-');
  const completed = hasReport(r.session);
  const sub = r.state === 'done' || r.state === 'scoring' ? `Completed ${fmtDate(r.completedAt)}` : resultDateText(r);
  return (
    <div className="wz-person">
      <span style={{ width: 16, display: 'inline-flex' }}>
        {eligible && <input type="checkbox" aria-label={`Select ${p.name}`} checked={checked} onChange={onCheck} />}
      </span>
      <Avatar name={p.name} size={32} />
      <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <span style={{ fontWeight: 500 }} className="wz-ellipsis">{p.name}</span>
        <span className="wz-sub" style={{ fontSize: 12 }}>{sub}</span>
      </span>
      <ScoreChip result={r} />
      <span className="acts">
        {completed && <button type="button" className="wz-linkbtn" onClick={() => onReport(r.session.id, examType)}>View report</button>}
        {r.state === 'inprogress' && <button type="button" className="wz-linkbtn" onClick={() => onReset(r.row)}>Reset</button>}
        {!completed && r.state !== 'inprogress' && !String(r.row.id).startsWith('synced-') && (
          <button type="button" className="wz-linkbtn" disabled={busyId === r.row.id} onClick={() => onSend(r.row)}>
            {busyId === r.row.id ? '…' : invite === 'pending' ? 'Send invite' : 'Resend'}
          </button>
        )}
      </span>
    </div>
  );
}

/**
 * Prototype drawer(): right side panel for one interview (exam set). The old
 * "Open Test" modal's content lives here: questions → Setup tab, candidates +
 * invites → Candidates tab (T0 #18). Footer: Delete interview + View full report.
 */
export default function InterviewPanel({
  campaign, examSet, data, hireBar, onClose, onAddCandidates, onViewReport, onDeleted,
  batches = [], onNewBatch, renderBatchesTab, groupByBatch,
}) {
  const toast = useToast();
  const panelRef = useRef(null);
  const [tab, setTab] = useState('summary');
  const [detail, setDetail] = useState(null); // exam set detail with questions
  const [selected, setSelected] = useState(() => new Set());
  const [busyId, setBusyId] = useState(null);
  const [sending, setSending] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const t = typeOfExamType(examSet.exam_type);
  const live = examSet.is_active !== false;
  useEscape(onClose);

  useEffect(() => { setTab('summary'); setSelected(new Set()); setError(''); }, [examSet.id]);
  useEffect(() => { panelRef.current?.focus(); }, [examSet.id]);

  useEffect(() => {
    if (tab !== 'setup' || detail?.id === examSet.id) return undefined;
    let cancelled = false;
    examSetAPI.get(campaign.id, examSet.id)
      .then((res) => { if (!cancelled) setDetail(res.data); })
      .catch(() => { if (!cancelled) setDetail({ id: examSet.id, questions: [] }); });
    return () => { cancelled = true; };
  }, [tab, campaign.id, examSet.id, detail?.id]);

  const entries = useMemo(
    () => data.people.filter((p) => p.results[examSet.id]).map((p) => ({ p, r: p.results[examSet.id] }))
      .sort((a, b) => a.p.name.localeCompare(b.p.name)),
    [data.people, examSet.id],
  );
  const s = useMemo(() => statsFor(entries.map((e) => e.r), hireBar), [entries, hireBar]);
  const maxBucket = Math.max(1, ...s.dist);
  const eligibleIds = entries
    .filter(({ r }) => String(r.row.invite_status || 'pending').toLowerCase() === 'pending' && !String(r.row.id).startsWith('synced-'))
    .map(({ r }) => r.row.id);
  const allEligible = eligibleIds.length > 0 && eligibleIds.every((id) => selected.has(id));

  const handleSend = async (row) => {
    setBusyId(row.id);
    setError('');
    try {
      await sendSingleInvite(row.id);
      toast(String(row.invite_status || 'pending').toLowerCase() === 'pending' ? 'Invite sent.' : 'Invite resent.');
      data.reloadAssignments();
    } catch (err) {
      setError(err.message || 'Failed to send invite.');
    } finally {
      setBusyId(null);
    }
  };

  const handleBulk = async () => {
    setSending(true);
    setError('');
    try {
      const res = await sendBulkInvite([...selected]);
      toast(`Invite sent to ${res.sent_count ?? selected.size} candidate(s).`);
      setSelected(new Set());
      data.reloadAssignments();
    } catch (err) {
      setError(err.message || 'Failed to send invites.');
    } finally {
      setSending(false);
    }
  };

  const handleReset = async (row) => {
    if (!window.confirm("Reset this candidate's session? They will be able to restart the interview.")) return;
    try {
      await resetCandidateSession(row.id);
      toast('Session reset — candidate can restart.');
      data.reloadAssignments();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete this ${t.legacyTitle || 'exam'}? This cannot be undone.`)) return;
    try {
      await examSetAPI.remove(campaign.id, examSet.id);
      onDeleted?.();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed to delete exam.');
    }
  };

  const setupKv = [
    ['Interview type', t.name],
    ['Questions', examSet.question_count ?? 0],
    ['Created', fmtDate(examSet.created_at)],
    ['Status', live ? 'Live' : 'Draft'],
  ];

  const personRows = (list) => list.map(({ p, r }) => (
    <PersonRow
      key={r.row.id}
      p={p}
      r={r}
      examType={examSet.exam_type}
      checked={selected.has(r.row.id)}
      onCheck={() => setSelected((prev) => { const n = new Set(prev); if (n.has(r.row.id)) n.delete(r.row.id); else n.add(r.row.id); return n; })}
      busyId={busyId}
      onSend={handleSend}
      onReset={handleReset}
      onReport={(sessionId, examType) => setReport({ sessionId, examType })}
    />
  ));

  let body;
  if (tab === 'summary') {
    body = (
      <>
        <div className="wz-stats4">
          {[['Assigned', s.total], ['Completed', s.done], ['Not started', s.notStarted], ['Avg score', fmtScore(s.avg)]].map(([k, v]) => (
            <div key={k}><b>{v}</b><span className="wz-sub" style={{ fontSize: 12 }}>{k}</span></div>
          ))}
        </div>
        {s.total ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <h3 style={{ fontSize: 14, fontWeight: 600 }}>Score distribution</h3>
              <span className="wz-sub">{s.above} of {s.scoredCount} at or above the hire bar</span>
            </div>
            <div className="wz-dist" aria-label="Score distribution">
              {s.dist.map((n, i) => (
                <div key={BUCKETS[i]}>
                  {n}
                  <i style={{ height: Math.round((n / maxBucket) * 88), background: i * 20 >= s.hireBar ? 'var(--wz-pass)' : 'var(--wz-below-bar)' }} />
                </div>
              ))}
            </div>
            <div className="wz-dist-labels">{BUCKETS.map((l) => <span key={l}>{l}</span>)}</div>
            <div className="wz-legend">
              <span><i style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--wz-pass)', display: 'inline-block' }} />At or above hire bar ({s.mixedHireBar ? `varies by batch, avg ${fmtScore(s.hireBar)}` : fmtScore(s.hireBar)})</span>
              <span><i style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--wz-below-bar)', display: 'inline-block' }} />Below hire bar</span>
            </div>
          </div>
        ) : (
          <div className="wz-empty" style={{ border: '1px dashed var(--wz-control)', borderRadius: 'var(--wz-r-lg)', padding: '32px 20px' }}>
            <h3 style={{ fontSize: 15, fontWeight: 600 }}>No candidates assigned</h3>
            <p style={{ maxWidth: '36ch', fontSize: 13 }}>
              Create a batch to invite candidates. Results and the score distribution appear here as they finish.
            </p>
            <Button need="exam.manage" variant="primary" icon="plus" onClick={onNewBatch}>New batch</Button>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600 }}>Setup</h3>
          <dl className="wz-kv">{setupKv.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}</dl>
        </div>
      </>
    );
  } else if (tab === 'batches') {
    body = renderBatchesTab?.(examSet);
  } else if (tab === 'people') {
    const groups = groupByBatch ? groupByBatch(examSet, entries) : [{ key: 'all', label: null, list: entries }];
    body = (
      <>
        {eligibleIds.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <label className="wz-check"><input type="checkbox" checked={allEligible} onChange={() => setSelected(allEligible ? new Set() : new Set(eligibleIds))} />Select everyone not invited yet</label>
            <Button need="invite.send" small variant="primary" icon="mail" disabled={sending || !selected.size} onClick={handleBulk}>
              {sending ? 'Sending…' : `Send invites (${selected.size})`}
            </Button>
          </div>
        )}
        {entries.length ? groups.map((g) => (g.list.length ? (
          <div key={g.key}>
            {g.label && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 4 }}>
                <b style={{ fontSize: 13 }}>{g.label}</b>
                <span className="wz-sub" style={{ fontSize: 12 }}>{g.list.length} candidate{g.list.length > 1 ? 's' : ''}</span>
              </div>
            )}
            {personRows(g.list)}
          </div>
        ) : null)) : <p className="wz-sub">No candidates assigned yet.</p>}
      </>
    );
  } else {
    const qs = detail?.id === examSet.id ? detail.questions || [] : null;
    const usesCoding = t.sets.includes('code');
    const usesText = t.sets.some((x) => x !== 'code');
    body = (
      <>
        <dl className="wz-kv" style={{ fontSize: 14, gap: '14px 16px' }}>
          {setupKv.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}
        </dl>
        {usesText && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600 }}>Questions{qs ? ` (${qs.length})` : ''}</h3>
            {qs === null ? <p className="wz-sub">Loading…</p> : qs.length ? (
              <ol style={{ display: 'flex', flexDirection: 'column' }}>
                {qs.map((q, i) => (
                  <li key={q.id || i} className="wz-person" style={{ alignItems: 'flex-start' }}>
                    <span className="wz-sub" style={{ width: 20 }}>{i + 1}</span>
                    <span style={{ flex: 1, lineHeight: 1.45, fontSize: 13 }}>{q.question}</span>
                    <span className="wz-sub" style={{ fontSize: 12, width: 90 }}>
                      {q.exam_category === 'english' ? (SKILL_AREA_LABELS[q.skill_area] || 'Proficiency') : String(q.type || 'technical').replace(/^./, (c) => c.toUpperCase())}
                    </span>
                    <span style={{ width: 84, flexShrink: 0 }}><DifficultyBars level={q.difficulty} /></span>
                  </li>
                ))}
              </ol>
            ) : <p className="wz-sub">No questions linked to this test.</p>}
          </div>
        )}
        {usesCoding && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600 }}>Coding problems ({data.codingQuestions.length})</h3>
            <p className="wz-sub" style={{ fontSize: 12 }}>Shared by every coding interview on this position.</p>
            {data.codingQuestions.map((cq, i) => (
              <div key={cq.id} className="wz-person">
                <span className="wz-sub" style={{ width: 20 }}>{i + 1}</span>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{cq.title}</span>
                <span className="wz-sub" style={{ fontSize: 12 }}>{(cq.languages?.length ? cq.languages : ['python']).map(codingLanguageLabel).join(', ')}</span>
              </div>
            ))}
          </div>
        )}
      </>
    );
  }

  const tabs = [['summary', 'Summary'], ['batches', `Batches (${batches.length})`], ['people', 'Candidates'], ['setup', 'Setup']];

  return (
    <>
      <div className="wz-scrim" onClick={onClose} />
      <aside ref={panelRef} tabIndex={-1} className="wz-drawer" role="dialog" aria-label={`${examSet.name} details`}>
        <div className="wz-drawer-head">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <FamilyTag fam={t.fam} />
              <h2 style={{ fontSize: 20 }}>{examSet.name}</h2>
              <span style={{ display: 'flex', gap: 14, fontSize: 13, color: 'var(--wz-muted)' }}>
                <StatusDot live={live} />
                <span>{t.name}</span>
                <span>{batches.length} batch{batches.length === 1 ? '' : 'es'}</span>
              </span>
            </div>
            <Button iconOnly icon="x" aria-label="Close" onClick={onClose} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
            <Button need="exam.manage" small icon="userAdd" onClick={onAddCandidates}>Add candidates</Button>
            <Button need="exam.manage" small icon="plus" onClick={onNewBatch}>New batch</Button>
          </div>
          <div className="wz-drawer-tabs" role="tablist">
            {tabs.map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{l}</button>
            ))}
          </div>
        </div>
        <div className="wz-drawer-body">
          {error && <div className="wz-error" role="alert">{error}</div>}
          {body}
        </div>
        <div className="wz-drawer-foot">
          <Button need="exam.manage" variant="danger" onClick={handleDelete}>Delete interview</Button>
          <Button variant="primary" icon="file" onClick={onViewReport}>View full report</Button>
        </div>
      </aside>
      {report && <ReportModal sessionId={report.sessionId} onClose={() => setReport(null)} />}
    </>
  );
}
